import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Search, CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Reveal, SectionHeading, Overline } from "@/components/Primitives";
import { SERVICE_AREAS } from "@/data/site";

const ALL_CITIES = SERVICE_AREAS.flatMap((a) => a.cities);

export default function ServiceAreas() {
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);

  const check = () => {
    if (!query.trim()) return;
    const found = ALL_CITIES.some((c) => c.toLowerCase().includes(query.trim().toLowerCase()));
    setResult(found);
  };

  return (
    <div>
      <section className="pt-36 pb-16 bg-brand-green">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading light overline="Service Areas" title="Local coverage across Metro Vancouver"
            subtitle="Enter your city to confirm we serve your neighborhood — fast, friendly, local technicians." />
          <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-lg">
            <Input data-testid="area-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && check()}
              placeholder="Enter your city (e.g. Surrey)"
              className="h-14 rounded-full bg-white border-0 px-6 text-base" />
            <Button data-testid="area-search-btn" onClick={check} className="h-14 rounded-full bg-brand-gold text-brand-green font-bold px-8 hover:bg-brand-goldHover">
              <Search className="h-5 w-5 mr-2" /> Check
            </Button>
          </div>
          {result !== null && (
            <div data-testid="area-result" className={`mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-sm ${result ? "bg-white text-brand-green" : "bg-white/10 text-white"}`}>
              {result ? <><CheckCircle2 className="h-5 w-5 text-brand-green" /> Great news — we serve your area!</> : <>We may still help — call +1 (672) 377-5455 to confirm.</>}
            </div>
          )}
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
          <div className="grid sm:grid-cols-2 gap-6">
            {SERVICE_AREAS.map((a, i) => (
              <Reveal key={a.region} delay={i * 0.08}>
                <div className="bg-white rounded-3xl p-7 border border-black/5 shadow-sm h-full">
                  <div className="flex items-center gap-3"><div className="h-10 w-10 rounded-xl bg-brand-green/5 flex items-center justify-center"><MapPin className="h-5 w-5 text-brand-green" /></div>
                    <h3 className="font-heading text-lg font-bold text-brand-green">{a.region}</h3></div>
                  <ul className="mt-4 space-y-2">
                    {a.cities.map((c) => (
                      <li key={c} className="flex items-center gap-2 text-sm text-brand-muted"><CheckCircle2 className="h-4 w-4 text-brand-gold" />{c}</li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="rounded-[2rem] overflow-hidden border border-black/5 shadow-lg h-full min-h-[400px]">
              <iframe title="EXORA Service Area Map"
                src="https://www.google.com/maps?q=Surrey,British+Columbia,Canada&output=embed"
                className="w-full h-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-brand-green">Don't see your city?</h2>
          <p className="text-brand-muted mt-3">We're expanding fast. Request an inspection and we'll let you know if we can help.</p>
          <Link to="/request-inspection"><Button className="rounded-full bg-brand-green text-white font-semibold h-14 px-10 mt-6 hover:bg-brand-greenHover">Request an Inspection <ArrowRight className="h-5 w-5 ml-2" /></Button></Link>
        </div>
      </section>
    </div>
  );
}
